// ─────────────────────────────────────────────────────────────────────────────
//  Document API — endpoint constants
//
//  All three API routes for the Documents module live here.
//  Import and use these anywhere instead of hard-coding URL strings.
//
//  Backend base URL is read from environment.apiUrl at runtime, so switching
//  environments (dev → prod) requires no changes here.
//
//  Usage example:
//      import { DOC_API } from './document-api.constants';
//      this.http.get(`${environment.apiUrl}${DOC_API.CASE_DATA(caseId)}`)
// ─────────────────────────────────────────────────────────────────────────────

/** URL prefix for all document endpoints */
export const DOC_API_PREFIX = '/api/documents';

export const DOC_API = {
    /**
     * @deprecated Use CASE_DATA_BY_TYPE instead
     * GET /api/documents/case-data/{caseId}
     */
    CASE_DATA: (caseId: number | string) =>
        `${DOC_API_PREFIX}/case-data/${caseId}`,

    /**
     * GET /api/case-data/{caseId}/{documentType}
     *
     * Returns document-specific patient / provider / offer variables.
     * documentType: 1=cover_letter, 2=acknowledgment, 3=ATF, 4=HIPAA
     *
     * @param caseId        The numeric case ID
     * @param documentType  1 | 2 | 3 | 4
     */
    CASE_DATA_BY_TYPE: (caseId: number | string, documentType: number) =>
        `/api/case-data/${caseId}/${documentType}`,

    /**
     * POST /api/documents/upload-signed
     *
     * Receives a signed PDF (multipart/form-data) and stores it in
     * Laravel's public disk under:
     *   storage/app/public/signed-documents/{caseId}/{docLabel}-{timestamp}.pdf
     *
     * Required form fields:
     *   - case_id     (integer)
     *   - document_id (0 | 1 | 2 | 3)
     *   - signed_at   (YYYY-MM-DD)
     *   - pdf_file    (binary PDF, max 20 MB)
     */
    UPLOAD_SIGNED: `${DOC_API_PREFIX}/upload-signed`,

    /**
     * POST /api/documents/render-signed
     *
     * Renders the signed PDF on the server using a headless-Chromium service
     * (Gotenberg). The backend templates the requested document, embeds the
     * signature PNG, converts to PDF, persists it under
     *   storage/app/public/signed-documents/{caseId}/{docLabel}-{timestamp}.pdf
     * and returns the binary PDF in the response body
     * (Content-Type: application/pdf).
     *
     * Request JSON body:
     *   - case_id        (integer)
     *   - document_type  (2 | 3 | 4 | 5)   // matches DOC_TYPE_MAP
     *   - signature_png  (string, data-URL or raw base64 of the patient's signature)
     *   - signed_at      (YYYY-MM-DD)
     *   - signer_name    (string)          // patient full name as printed on the doc
     */
    RENDER_SIGNED: `${DOC_API_PREFIX}/render-signed`,

} as const;

// ── Document ID map (matches DocumentId = 0 | 1 | 2 | 3) ──────────────────
export const DOCUMENT_LABELS: Record<0 | 1 | 2 | 3, string> = {
    0: 'Cover Letter',
    1: 'Acknowledgment of Assignment',
    2: 'Assignment of Trust Funds, Obligations & Responsibilities',
    3: 'HIPAA Authorization',
};

/**
 * Maps frontend document index (0-based) to backend API document_type (1-based).
 * Frontend index 0 (Acknowledgment)       → API type 2
 * Frontend index 1 (ATF)                  → API type 3
 * Frontend index 2 (HIPAA)                → API type 4
 * Frontend index 3 (Release of Lien)      → API type 5
 */
export const DOC_TYPE_MAP: Record<0 | 1 | 2 | 3, number> = {
    0: 2,
    1: 3,
    2: 4,
    3: 5,
};
